import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  Settings,
  Hd,
} from "@mui/icons-material";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { getZegoToken } from "../../../services/api/zego.api";

const VideoCallWrapper = styled(Box)<{}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  transition: all 0.3s ease-in-out;
`;

const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  width: 100%;
`;

const COLORS = {
  bgPanel: "#141414",
  bgAlt: "#1b1b1b",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
  green: "#4CAF50",
  greenHover: "#45a049",
};

interface PerformerStreamProps {
  userId: string;
  videoId: string;
  onCallEnd: () => void;
  zg: ZegoExpressEngine | null;
  performerName?: string;
}

const PerformerStream: React.FC<PerformerStreamProps> = ({
  userId,
  videoId,
  onCallEnd,
  zg,
  performerName = "Performer",
}) => {
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
    null
  );
  const [videoQuality, setVideoQuality] = useState<
    "240p" | "360p" | "480p" | "720p"
  >("720p");
  const [qualityAuto, setQualityAuto] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [isLive, setIsLive] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "connecting" | "connected" | "disconnected"
  >("disconnected");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [videoToken, setVideoToken] = useState<string | null>(null);

  // Zego logic
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isJoined, setIsJoined] = useState(false);

  const joinRoom = async () => {
    try {
      if (isJoined || !zg || !videoToken) return;
      console.log("Performer video Token is", videoToken);

      setConnectionStatus("connecting");

      await zg.loginRoom(
        videoId,
        videoToken,
        { userID: userId, userName: performerName },
        { userUpdate: true }
      );

      // Create and publish stream
      const stream = await zg.createStream({
        camera: {
          audio: micOn,
          video: cameraOn,
          videoQuality: 4,
          bitrate: 3000,
          frameRate: 30,
          width: 1280,
          height: 720,
        },
      });

      if (localVideoRef.current && stream) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.muted = true;
        await localVideoRef.current.play();
        zg.startPublishingStream("stream_" + userId, stream);
        setLocalStream(stream);
      }

      setIsJoined(true);
      setIsLive(true);
      setConnectionStatus("connected");

      setSnackbar({
        open: true,
        message: "Successfully connected to the stage!",
        severity: "success",
      });
    } catch (error) {
      console.log(error, "Performer join room error");
      setConnectionStatus("disconnected");
    }
  };

  const cleanup = () => {
    if (zg && isJoined) {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
        zg.stopPublishingStream("stream_" + userId);
      }

      zg.logoutRoom(videoId);
      zg.destroyEngine();
      setIsJoined(false);
      setIsLive(false);
      setConnectionStatus("disconnected");
    }
  };

  useEffect(() => {
    if (zg) joinRoom();
    // Cleanup on unmount
    return () => {
      cleanup();
    };
    // eslint-disable-next-line
  }, [zg, videoToken]);

  useEffect(() => {
    if (videoToken) return;
    const token = localStorage.getItem("videoToken");
    if (token) {
      setVideoToken(token);
    } else {
      getZegoToken(userId!)
        .then((token: string) => {
          console.log("Performer token", token);
          setVideoToken(token);
        })
        .catch((err: any) => {
          console.log(err.message || "Failed to fetch token");
          setSnackbar({
            open: true,
            message: "Failed to get streaming token",
            severity: "error",
          });
        });
    }
  }, [videoId, videoToken, userId]);

  const controlButtonStyle = {
    height: isMobile ? "36px" : "48px",
    width: isMobile ? "36px" : "48px",
    padding: isMobile ? "6px" : "8px",
    backgroundColor: "#EEF2FF",
    color: "#000",
    borderRadius: "4px",
    zIndex: 3,
  };

  const controlButtonStyleDestructive = {
    height: isMobile ? "36px" : "48px",
    width: isMobile ? "36px" : "48px",
    padding: isMobile ? "6px" : "8px",
    backgroundColor: COLORS.red,
    color: "#fff",
    borderRadius: "4px",
    zIndex: 3,
  };

  const handleEndCall = () => {
    cleanup();
    onCallEnd();
  };

  // Video quality change
  const handleQualityChange = async (
    quality: "240p" | "360p" | "480p" | "720p"
  ) => {
    setVideoQuality(quality);
    setQualityAuto(false);
    setSnackbar({
      open: true,
      message: `Video quality changed to ${quality}`,
      severity: "success",
    });
    setSettingsAnchor(null);
  };

  const toggleAudio = () => {
    try {
      if (!localStream) return;
      localStream
        .getAudioTracks()
        .forEach((track) => (track.enabled = !track.enabled));
      setMic((prev) => !prev);
      setSnackbar({
        open: true,
        message: micOn ? "Microphone muted" : "Microphone unmuted",
        severity: "success",
      });
    } catch (error) {
      console.error(error, "Toggle audio");
    }
  };

  const toggleVideo = () => {
    try {
      if (!localStream) return;
      localStream
        .getVideoTracks()
        .forEach((track) => (track.enabled = !track.enabled));
      setCamera((prev) => !prev);
      setSnackbar({
        open: true,
        message: cameraOn ? "Camera turned off" : "Camera turned on",
        severity: "success",
      });
    } catch (error) {
      console.error(error, "Toggle video");
    }
  };

  return (
    <>
      <MainContainer>
        <VideoCallWrapper
          sx={{
            position: "relative",
            top: "auto",
            left: "auto",
            height: "auto",
            zIndex: "auto",
            backgroundColor: "transparent",
            width: "100%",
            opacity: 1,
            transition: "all 0.3s ease-in-out",
          }}
        >
          {/* Status Header */}
          <Paper
            elevation={0}
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              right: 16,
              zIndex: 1000,
              bgcolor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip
                  size="small"
                  label={isLive ? "LIVE" : "OFFLINE"}
                  sx={{
                    bgcolor: isLive
                      ? "rgba(76, 175, 80, 0.2)"
                      : "rgba(244, 67, 54, 0.2)",
                    border: "1px solid",
                    borderColor: isLive ? COLORS.green : COLORS.red,
                    color: isLive ? COLORS.green : COLORS.red,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  size="small"
                  label={connectionStatus}
                  sx={{
                    bgcolor:
                      connectionStatus === "connected"
                        ? "rgba(76, 175, 80, 0.2)"
                        : connectionStatus === "connecting"
                          ? "rgba(255, 193, 7, 0.2)"
                          : "rgba(244, 67, 54, 0.2)",
                    border: "1px solid",
                    borderColor:
                      connectionStatus === "connected"
                        ? COLORS.green
                        : connectionStatus === "connecting"
                          ? "#FFC107"
                          : COLORS.red,
                    color:
                      connectionStatus === "connected"
                        ? COLORS.green
                        : connectionStatus === "connecting"
                          ? "#FFC107"
                          : COLORS.red,
                    fontWeight: 500,
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Box sx={{ width: "100%", height: "100%"}}>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              width="100%"
              height="100%"
              style={{
                borderRadius: 8,
                backgroundColor: "#000",
                height: "100%",
              }}
            />
          </Box>

          {/* Control Buttons */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
            gap={"16px"}
            sx={{
              p: 2,
              position: "absolute",
              bottom: "2%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
            }}
          >
            <ControlButton
              onClick={toggleAudio}
              icon={micOn ? <Mic /> : <MicOff />}
              sx={{
                ...controlButtonStyle,
                backgroundColor: micOn ? "#EEF2FF" : COLORS.red,
                color: micOn ? "#000" : "#fff",
                "&:hover": {
                  backgroundColor: micOn ? "#E8EAF6" : COLORS.redHover,
                },
              }}
            />
            <ControlButton
              onClick={toggleVideo}
              icon={cameraOn ? <Videocam /> : <VideocamOff />}
              sx={{
                ...controlButtonStyle,
                backgroundColor: cameraOn ? "#EEF2FF" : COLORS.red,
                color: cameraOn ? "#000" : "#fff",
                "&:hover": {
                  backgroundColor: cameraOn ? "#E8EAF6" : COLORS.redHover,
                },
              }}
            />
            <ControlButton
              onClick={() =>
                setSettingsAnchor(document.activeElement as HTMLElement)
              }
              icon={<Settings />}
              sx={{
                ...controlButtonStyle,
                "&:hover": {
                  backgroundColor: "#E8EAF6",
                },
              }}
            />
            <ControlButton
              onClick={handleEndCall}
              icon={<CallEnd />}
              sx={{
                ...controlButtonStyleDestructive,
                "&:hover": {
                  backgroundColor: COLORS.redHover,
                },
              }}
            />

            <Menu
              anchorEl={settingsAnchor}
              open={Boolean(settingsAnchor)}
              onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              {(["240p", "360p", "480p", "720p"] as const).map((q) => (
                <MenuItem key={q} onClick={() => handleQualityChange(q)}>
                  <ListItemIcon>
                    <Hd fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={q}
                    secondary={videoQuality === q ? "Current" : ""}
                  />
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  setQualityAuto(true);
                  setSettingsAnchor(null);
                  setSnackbar({
                    open: true,
                    message: "Auto quality enabled",
                    severity: "success",
                  });
                }}
              >
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Auto"
                  secondary={qualityAuto ? "Current" : ""}
                />
              </MenuItem>
            </Menu>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          />
        </VideoCallWrapper>
      </MainContainer>
    </>
  );
};

const ControlButton = ({
  onClick,
  icon,
  sx = {},
}: {
  onClick: () => void;
  icon: React.ReactNode;
  sx: Record<string, any>;
}) => {
  return (
    <IconButton
      onClick={onClick}
      sx={{
        ...sx,
      }}
    >
      {icon}
    </IconButton>
  );
};

export default PerformerStream;
